import { useEffect, useRef, useState } from "react";
import { api, eur, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Upload, Loader2, ImageIcon, Sun, CalendarDays, Clock, RotateCcw } from "lucide-react";
import { WeekCalendar, StockThreshold } from "@/components/dashboard/SpecialsWeekCalendar";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
const L = ({ label, children }) => <div><label className="text-xs font-semibold uppercase tracking-wider text-[#8A918D]">{label}</label><div className="mt-1">{children}</div></div>;

export default function DailySpecialsPanel({ slug, products, refreshKey }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false); // false | true | "YYYY-MM-DD" (date pré-remplie)
  const load = () => api.listSpecials(slug).then(setList).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, refreshKey]);

  const toggleActive = async (s) => {
    try { await api.updateSpecial(slug, s.id, { active: !s.active }); toast.success(!s.active ? "Plat du jour activé" : "Plat du jour désactivé"); load(); } catch { toast.error("Erreur"); }
  };
  const quickQty = async (s, delta) => {
    const q = Math.max(0, (s.quantity || 0) + delta);
    try { await api.updateSpecial(slug, s.id, { quantity: q }); load(); } catch { toast.error("Erreur"); }
  };
  const reactivate = async (s) => {
    try { await api.updateSpecial(slug, s.id, { start_date: today(), end_date: today(), active: true, ...(s.quantity != null ? { quantity: s.quantity } : {}) }); toast.success("Reprogrammé pour aujourd'hui"); load(); } catch { toast.error("Erreur"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer « ${s.name} » ?`)) return;
    try { await api.deleteSpecial(slug, s.id); toast.success("Supprimé"); load(); } catch { toast.error("Erreur"); }
  };

  const live = list.filter((s) => s.live);
  const others = list.filter((s) => !s.live);

  const Card = ({ s }) => (
    <div data-testid={`special-row-${s.id}`} className={`flex gap-3 rounded-2xl border p-3 ${s.live ? "bg-[#14271F] border-[#C69C4A]/50" : "bg-[#111F1A] border-[#1B3028]"}`}>
      {s.image ? <img src={fileUrl(s.image)} alt={s.name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
        : <div className="w-20 h-20 rounded-xl shrink-0 grid place-items-center bg-[#16241E] text-[#C69C4A]"><ImageIcon className="w-5 h-5" /></div>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-stone-100 truncate">{s.name}</p>
          <span className="text-sm font-bold text-[#C69C4A]">{eur(s.price)}</span>
          {s.live && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-emerald-600/20 text-emerald-300 border border-emerald-500/40">En ligne</span>}
          {s.sold_out && <span data-testid={`special-soldout-badge-${s.id}`} className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-red-600/20 text-red-300 border border-red-500/40">Épuisé</span>}
          {!s.active && <span className="text-[10px] rounded-full px-2 py-0.5 bg-stone-700 text-stone-300">Désactivé</span>}
        </div>
        <p className="text-[11px] text-stone-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{s.start_date || "∞"} → {s.end_date || "∞"}</span>
          <span>{(s.weekdays || []).length ? s.weekdays.map((d) => DAYS[d]).join(" ") : "Tous les jours"}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{s.start_time || "00:00"}–{s.end_time || "23:59"}</span>
        </p>
        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="flex items-center gap-2">
            <Switch data-testid={`special-active-${s.id}`} checked={!!s.active} onCheckedChange={() => toggleActive(s)} className="data-[state=checked]:bg-emerald-600" />
            {s.quantity != null ? (
              <span className="flex items-center gap-1 text-[11px] text-stone-300">
                <button data-testid={`special-qty-dec-${s.id}`} onClick={() => quickQty(s, -1)} className="w-6 h-6 rounded-full bg-[#1B3028] hover:bg-[#24402F]">−</button>
                <span data-testid={`special-remaining-${s.id}`} className="font-semibold">{s.remaining ?? s.quantity} / {s.quantity}</span>
                <button data-testid={`special-qty-inc-${s.id}`} onClick={() => quickQty(s, 1)} className="w-6 h-6 rounded-full bg-[#1B3028] hover:bg-[#24402F]">+</button>
              </span>
            ) : <span className="text-[11px] text-stone-500">Quantité illimitée</span>}
          </div>
          <div className="flex items-center gap-1">
            {!s.live && <button data-testid={`special-reactivate-${s.id}`} onClick={() => reactivate(s)} className="text-stone-400 hover:text-emerald-400 p-1" title="Reprogrammer aujourd'hui"><RotateCcw className="w-4 h-4" /></button>}
            <button data-testid={`special-edit-${s.id}`} onClick={() => setEditing(s)} className="text-stone-400 hover:text-[#C69C4A] p-1" title="Modifier"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`special-delete-${s.id}`} onClick={() => remove(s)} className="text-stone-400 hover:text-red-400 p-1" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="specials-panel">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-serif-display text-2xl text-stone-100 flex items-center gap-2"><Sun className="w-5 h-5 text-[#C69C4A]" /> Plats du jour</h2>
          <p className="text-stone-400 text-sm">Programmez votre semaine : les plats apparaissent et disparaissent automatiquement côté client (heure de Paris).</p>
        </div>
        <Button data-testid="special-add" onClick={() => setCreating(true)} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nouveau plat du jour</Button>
      </div>
      <StockThreshold slug={slug} />
      <WeekCalendar slug={slug} specials={list} onCreateAt={(d) => setCreating(d)} onEdit={(s) => setEditing(s)} onChanged={load} />
      {live.length > 0 && <><h3 className="text-xs font-bold uppercase tracking-wider text-emerald-300 mb-2">En ligne maintenant</h3><div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">{live.map((s) => <Card key={s.id} s={s} />)}</div></>}
      <h3 className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-2">Programmés / passés</h3>
      {others.length === 0 ? <p className="text-sm text-stone-500">Aucun autre plat du jour.</p> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{others.map((s) => <Card key={s.id} s={s} />)}</div>}
      {(creating || editing) && <SpecialForm slug={slug} products={products} special={editing} presetDate={typeof creating === "string" ? creating : null} onClose={() => { setCreating(false); setEditing(null); }} onDone={() => { setCreating(false); setEditing(null); load(); }} />}
    </div>
  );
}

function SpecialForm({ slug, products, special, presetDate, onClose, onDone }) {
  const isEdit = !!special;
  const [form, setForm] = useState({
    name: special?.name || "", description: special?.description || "", price: special?.price ?? "", image: special?.image || "",
    product_id: special?.product_id || "", vat_rate: special?.vat_rate ?? null,
    start_date: special?.start_date || presetDate || today(), end_date: special?.end_date || presetDate || today(),
    weekdays: special?.weekdays || [], start_time: special?.start_time || "", end_time: special?.end_time || "",
    quantity: special?.quantity ?? "", active: special?.active ?? true, label_style: special?.label_style || "success",
    low_stock_threshold: special?.low_stock_threshold ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickProduct = (id) => {
    const p = products.find((x) => x.id === id);
    if (!p) { set("product_id", ""); return; }
    setForm((f) => ({ ...f, product_id: id, name: p.name, description: p.description || "", image: p.image || "", price: f.price === "" ? p.price : f.price, vat_rate: p.vat_rate ?? null }));
  };
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try { const res = await api.uploadPhoto(slug, file); set("image", fileUrl(res.url)); } catch { toast.error("Échec de l'upload"); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.name || form.price === "") { toast.error("Nom et prix requis"); return; }
    setSaving(true);
    try {
      const body = { ...form, price: parseFloat(form.price), quantity: form.quantity === "" ? null : parseInt(form.quantity, 10), product_id: form.product_id || null,
        low_stock_threshold: form.low_stock_threshold === "" ? null : parseInt(form.low_stock_threshold, 10),
        start_date: form.start_date || null, end_date: form.end_date || null, start_time: form.start_time || null, end_time: form.end_time || null };
      if (isEdit) {
        const upd = Object.fromEntries(Object.entries(body).filter(([, v]) => v !== null));
        if (body.quantity === null) upd.clear_quantity = true;
        if (body.low_stock_threshold === null) upd.clear_low_stock_threshold = true;
        if (!body.start_date && !body.end_date) upd.clear_dates = true;
        if (!body.start_time && !body.end_time) upd.clear_times = true;
        await api.updateSpecial(slug, special.id, upd);
      } else await api.createSpecial(slug, body);
      toast.success(isEdit ? "Plat du jour modifié" : "Plat du jour créé");
      onDone();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="special-form" className="max-w-lg rounded-3xl bg-[#FBF9F5] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#123026]">{isEdit ? "Modifier le plat du jour" : "Nouveau plat du jour"}</DialogTitle><DialogDescription className="sr-only">Formulaire plat du jour</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <L label="Partir d'un plat existant (facultatif)">
            <select data-testid="sf-product" value={form.product_id} onChange={(e) => pickProduct(e.target.value)} className="w-full bg-white border border-[#EAE5DC] rounded-xl px-3 py-2 text-sm">
              <option value="">— Nouveau plat —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {eur(p.price)}</option>)}
            </select>
          </L>
          <L label="Nom"><Input data-testid="sf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white rounded-xl" /></L>
          <L label="Description"><Textarea data-testid="sf-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-white rounded-xl resize-none" /></L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Prix spécial (€)"><Input data-testid="sf-price" type="number" step="0.10" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-white rounded-xl" /></L>
            <L label="Quantité (vide = illimité)"><Input data-testid="sf-qty" type="number" min="0" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="bg-white rounded-xl" /></L>
          </div>
          {form.quantity !== "" && (
            <L label="Seuil d'alerte stock faible pour ce plat (vide = seuil du restaurant)"><Input data-testid="sf-threshold" type="number" min="0" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className="bg-white rounded-xl" /></L>
          )}
          <L label="Photo">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-xl bg-white border border-[#EAE5DC] overflow-hidden grid place-items-center shrink-0">{form.image ? <img src={fileUrl(form.image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-[#C9CFC9]" />}</div>
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="sf-file" />
                <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="rounded-full w-full">{uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Importer une photo</Button>
                <Input data-testid="sf-image" value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="ou coller une URL" className="bg-white rounded-xl mt-2 text-xs" />
              </div>
            </div>
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Du"><Input data-testid="sf-start-date" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="bg-white rounded-xl" /></L>
            <L label="Au"><Input data-testid="sf-end-date" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="bg-white rounded-xl" /></L>
          </div>
          <L label="Jours (aucun = tous les jours)">
            <div className="flex gap-1.5 flex-wrap">
              {DAYS.map((d, i) => {
                const on = form.weekdays.includes(i);
                return <button key={d} type="button" data-testid={`sf-day-${i}`} onClick={() => set("weekdays", on ? form.weekdays.filter((x) => x !== i) : [...form.weekdays, i].sort())} className={`min-w-[44px] h-10 rounded-full text-xs font-bold border ${on ? "bg-[#123026] text-white border-[#123026]" : "bg-white text-[#123026] border-[#EAE5DC]"}`}>{d}</button>;
              })}
            </div>
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label="De (heure)"><Input data-testid="sf-start-time" type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="bg-white rounded-xl" /></L>
            <L label="À (heure)"><Input data-testid="sf-end-time" type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className="bg-white rounded-xl" /></L>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Switch data-testid="sf-active" checked={form.active} onCheckedChange={(v) => set("active", v)} className="data-[state=checked]:bg-emerald-600" />
            <span className="text-sm text-[#5C615E]">Mise en avant active</span>
          </div>
        </div>
        <div className="flex gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">Annuler</Button>
          <Button data-testid="sf-save" onClick={save} disabled={saving} className="flex-1 rounded-full bg-[#123026] hover:bg-[#0A1C16] text-white">{saving ? "..." : "Enregistrer"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
